import React, { useState, useMemo, useCallback } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { getArtifactProjectId } from '../firebaseInit';
import * as firestorePaths from '../utils/firestorePaths';
import { useNotification } from '../contexts/NotificationContext';
import {
  PLANS,
  SUBSCRIPTION_STATUSES,
  getSubscriptionStatusDisplay,
  getEffectiveAgentLimit,
  getEffectiveEvalLimit,
  isWhiteLabelEnabled,
  getTrialDaysRemaining,
  getLifetimeSubscription
} from '../utils/usageTracking';
import Button from './shared/Button';

const PLAN_COLORS = {
  starter: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  pro: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  pro_plus: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' }
};

const STATUS_COLORS = {
  active: { bg: 'bg-green-100', text: 'text-green-800' },
  lifetime: { bg: 'bg-purple-100', text: 'text-purple-800' },
  trialing: { bg: 'bg-blue-100', text: 'text-blue-800' },
  past_due: { bg: 'bg-red-100', text: 'text-red-800' },
  canceled: { bg: 'bg-gray-100', text: 'text-gray-800' }
};

const SubscriptionsAdmin = ({ teams, users, db, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useNotification();

  const [filters, setFilters] = useState({ search: '', plan: '', status: '' });
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showGrantLifetime, setShowGrantLifetime] = useState(null);
  const [showAddCredits, setShowAddCredits] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(10);

  const appId = getArtifactProjectId();

  const getTeamAgentCount = useCallback((teamId) => {
    return users.filter(u => u.teamId === teamId && u.role === 'basic').length;
  }, [users]);

  const getTeamMemberCount = useCallback((teamId) => {
    return users.filter(u => u.teamId === teamId).length;
  }, [users]);

  const teamsWithSubs = useMemo(() => {
    return teams.map(team => {
      const sub = team.subscription || null;
      const agentCount = getTeamAgentCount(team.id);
      const memberCount = getTeamMemberCount(team.id);
      const planConfig = sub ? (PLANS[sub.plan] || PLANS.starter) : null;
      const statusDisplay = getSubscriptionStatusDisplay(sub);
      const effectiveAgentLimit = sub ? getEffectiveAgentLimit(sub) : 0;
      const effectiveEvalLimit = sub ? getEffectiveEvalLimit(sub) : 0;
      const whiteLabel = sub ? isWhiteLabelEnabled(sub) : false;

      return {
        ...team,
        sub,
        agentCount,
        memberCount,
        planConfig,
        statusDisplay,
        effectiveAgentLimit,
        effectiveEvalLimit,
        whiteLabel
      };
    });
  }, [teams, getTeamAgentCount, getTeamMemberCount]);

  const filteredTeams = useMemo(() => {
    return teamsWithSubs.filter(team => {
      const searchMatch = !filters.search ||
        team.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        team.leadEmail?.toLowerCase().includes(filters.search.toLowerCase());
      const planMatch = !filters.plan || team.sub?.plan === filters.plan;
      const statusMatch = !filters.status || team.sub?.status === filters.status;
      return searchMatch && planMatch && statusMatch;
    });
  }, [teamsWithSubs, filters]);

  const stats = useMemo(() => {
    const byPlan = { starter: 0, pro: 0, pro_plus: 0, none: 0 };
    const byStatus = { active: 0, lifetime: 0, trialing: 0, past_due: 0, canceled: 0, none: 0 };
    let estimatedMRR = 0;

    teamsWithSubs.forEach(team => {
      if (team.sub) {
        byPlan[team.sub.plan] = (byPlan[team.sub.plan] || 0) + 1;
        byStatus[team.sub.status] = (byStatus[team.sub.status] || 0) + 1;

        if (team.sub.status === 'active' && team.sub.plan) {
          const plan = PLANS[team.sub.plan];
          if (plan) {
            if (team.sub.billingInterval === 'year') {
              estimatedMRR += Math.round(plan.yearlyCost / 12);
            } else {
              estimatedMRR += plan.monthlyCost;
            }
          }
          const extraAgents = team.sub.addons?.extraAgents || 0;
          estimatedMRR += extraAgents * 20;
          if (team.sub.addons?.whiteLabelEnabled && team.sub.plan !== 'pro_plus') {
            estimatedMRR += 99;
          }
        }
      } else {
        byPlan.none++;
        byStatus.none++;
      }
    });

    return { byPlan, byStatus, estimatedMRR, total: teamsWithSubs.length };
  }, [teamsWithSubs]);

  const handleChangePlan = useCallback(async (teamId, newPlan) => {
    setSaving(true);
    try {
      const planConfig = PLANS[newPlan];
      if (!planConfig) throw new Error('Invalid plan');

      const teamPath = firestorePaths.getTeamPath(appId, teamId);
      await setDoc(doc(db, teamPath), {
        subscription: {
          plan: newPlan,
          planName: planConfig.name,
          evalLimit: planConfig.monthlyLimit,
          agentLimit: planConfig.agentLimit,
          updatedAt: new Date()
        }
      }, { merge: true });

      showSuccess(`Plan updated to ${planConfig.name}`);
      setEditingTeam(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error changing plan:', error);
      showError(`Failed to change plan: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [appId, db, showSuccess, showError, onRefresh]);

  const handleGrantLifetime = useCallback(async (teamId) => {
    setSaving(true);
    try {
      const teamPath = firestorePaths.getTeamPath(appId, teamId);
      const lifetimeSub = getLifetimeSubscription();
      await setDoc(doc(db, teamPath), { subscription: lifetimeSub }, { merge: true });

      showSuccess('Lifetime license granted successfully!');
      setShowGrantLifetime(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error granting lifetime:', error);
      showError(`Failed to grant lifetime: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [appId, db, showSuccess, showError, onRefresh]);

  const handleAddCredits = useCallback(async (teamId) => {
    if (creditsToAdd <= 0) {
      showWarning('Please enter a valid number of credits');
      return;
    }

    setSaving(true);
    try {
      const team = teamsWithSubs.find(t => t.id === teamId);
      const currentBonus = team?.sub?.addons?.bonusEvalCredits || 0;
      const teamPath = firestorePaths.getTeamPath(appId, teamId);

      await setDoc(doc(db, teamPath), {
        subscription: {
          addons: {
            bonusEvalCredits: currentBonus + creditsToAdd
          },
          updatedAt: new Date()
        }
      }, { merge: true });

      showSuccess(`Added ${creditsToAdd} bonus evaluation credits`);
      setShowAddCredits(null);
      setCreditsToAdd(10);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error adding credits:', error);
      showError(`Failed to add credits: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [appId, db, creditsToAdd, teamsWithSubs, showSuccess, showError, showWarning, onRefresh]);

  const handleUpdateSubscriptionField = useCallback(async (teamId, field, value) => {
    setSaving(true);
    try {
      const teamPath = firestorePaths.getTeamPath(appId, teamId);
      const updateData = { subscription: { [field]: value, updatedAt: new Date() } };
      await setDoc(doc(db, teamPath), updateData, { merge: true });

      showSuccess(`Subscription updated`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating subscription:', error);
      showError(`Failed to update: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [appId, db, showSuccess, showError, onRefresh]);

  const handleChangeStatus = useCallback(async (teamId, newStatus) => {
    setSaving(true);
    try {
      const teamPath = firestorePaths.getTeamPath(appId, teamId);
      await setDoc(doc(db, teamPath), {
        subscription: {
          status: newStatus,
          updatedAt: new Date()
        }
      }, { merge: true });

      showSuccess(`Status changed to ${newStatus}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error changing status:', error);
      showError(`Failed to change status: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [appId, db, showSuccess, showError, onRefresh]);

  const formatDate = (dateVal) => {
    if (!dateVal) return '—';
    const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total Teams</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Active</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{stats.byStatus.active}</div>
          {stats.byStatus.lifetime > 0 && (
            <div className="text-xs text-purple-600">+{stats.byStatus.lifetime} lifetime</div>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Trialing</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{stats.byStatus.trialing}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Needs Attention</div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {stats.byStatus.past_due + stats.byStatus.canceled + stats.byStatus.none}
          </div>
          {stats.byStatus.past_due > 0 && (
            <div className="text-xs text-red-600">{stats.byStatus.past_due} past due</div>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Est. MRR</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">${stats.estimatedMRR.toLocaleString()}</div>
        </div>
      </div>

      {/* Plan Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium text-gray-700">Plans:</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-400"></span>
            Starter ({stats.byPlan.starter})
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            Pro ({stats.byPlan.pro})
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
            Business ({stats.byPlan.pro_plus})
          </span>
          {stats.byPlan.none > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400"></span>
              No Plan ({stats.byPlan.none})
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search teams or leads..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filters.plan}
          onChange={(e) => setFilters(prev => ({ ...prev, plan: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Plans</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="pro_plus">Business</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="lifetime">Lifetime</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past Due</option>
          <option value="canceled">Canceled</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFilters({ search: '', plan: '', status: '' })}
        >
          Clear
        </Button>
        <span className="text-sm text-gray-500">
          {filteredTeams.length} of {stats.total} teams
        </span>
      </div>

      {/* Teams Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agents</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Evaluations</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Add-ons</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTeams.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-gray-500">
                    {stats.total === 0 ? 'No teams found.' : 'No teams match the current filters.'}
                  </td>
                </tr>
              ) : (
                filteredTeams.map(team => (
                  <React.Fragment key={team.id}>
                    <tr
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${expandedTeam === team.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                    >
                      {/* Expand Arrow */}
                      <td className="px-3 py-2 text-gray-400">
                        <svg
                          className={`w-4 h-4 transition-transform ${expandedTeam === team.id ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </td>

                      {/* Team */}
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900">{team.name}</div>
                        <div className="text-xs text-gray-500">{team.leadEmail || '—'}</div>
                      </td>

                      {/* Plan */}
                      <td className="px-3 py-2">
                        {team.sub?.plan ? (
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${PLAN_COLORS[team.sub.plan]?.bg || 'bg-gray-100'} ${PLAN_COLORS[team.sub.plan]?.text || 'text-gray-700'}`}>
                            {team.planConfig?.name || team.sub.plan}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No plan</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2">
                        {team.sub ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[team.sub.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[team.sub.status]?.text || 'text-gray-700'}`}>
                            {team.statusDisplay.icon} {team.statusDisplay.label}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⚠️ None
                          </span>
                        )}
                      </td>

                      {/* Agents */}
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-900">
                          {team.agentCount} / {team.effectiveAgentLimit === null ? '∞' : team.effectiveAgentLimit}
                        </div>
                        <div className="text-xs text-gray-500">{team.memberCount} total members</div>
                      </td>

                      {/* Evaluations */}
                      <td className="px-3 py-2">
                        {team.sub ? (
                          <>
                            <div className="text-sm text-gray-900">
                              {team.sub.usage?.evaluationsUsed || 0} / {team.effectiveEvalLimit}
                            </div>
                            <div className="w-20 bg-gray-200 rounded-full h-1.5 mt-1">
                              <div
                                className={`h-1.5 rounded-full ${
                                  ((team.sub.usage?.evaluationsUsed || 0) / team.effectiveEvalLimit) >= 0.9
                                    ? 'bg-red-500'
                                    : ((team.sub.usage?.evaluationsUsed || 0) / team.effectiveEvalLimit) >= 0.7
                                      ? 'bg-yellow-500'
                                      : 'bg-blue-500'
                                }`}
                                style={{ width: `${Math.min(100, ((team.sub.usage?.evaluationsUsed || 0) / team.effectiveEvalLimit) * 100)}%` }}
                              />
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Billing */}
                      <td className="px-3 py-2">
                        {team.sub?.status === 'lifetime' ? (
                          <span className="text-xs text-purple-600 font-medium">👑 Free</span>
                        ) : team.sub?.billingInterval ? (
                          <div>
                            <div className="text-sm text-gray-900 capitalize">{team.sub.billingInterval}ly</div>
                            {team.planConfig && (
                              <div className="text-xs text-gray-500">
                                ${team.sub.billingInterval === 'year' ? team.planConfig.yearlyCost : team.planConfig.monthlyCost}/{team.sub.billingInterval === 'year' ? 'yr' : 'mo'}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Add-ons */}
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {team.whiteLabel && (
                            <span className="inline-flex px-1.5 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">
                              🎨 WL
                            </span>
                          )}
                          {(team.sub?.addons?.extraAgents || 0) > 0 && (
                            <span className="inline-flex px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                              +{team.sub.addons.extraAgents} agents
                            </span>
                          )}
                          {(team.sub?.addons?.bonusEvalCredits || 0) > 0 && (
                            <span className="inline-flex px-1.5 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                              +{team.sub.addons.bonusEvalCredits} evals
                            </span>
                          )}
                          {!team.whiteLabel && !(team.sub?.addons?.extraAgents) && !(team.sub?.addons?.bonusEvalCredits) && (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAddCredits(team.id)}
                            className="text-xs text-green-600"
                            aria-label="Add evaluation credits"
                          >
                            +Credits
                          </Button>
                          {team.sub?.status !== 'lifetime' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowGrantLifetime(team.id)}
                              className="text-xs text-purple-600"
                              aria-label="Grant lifetime license"
                            >
                              👑
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Detail Row */}
                    {expandedTeam === team.id && (
                      <tr onClick={(e) => e.stopPropagation()}>
                        <td colSpan={9} className="px-3 py-0">
                          <ExpandedTeamDetail
                            team={team}
                            saving={saving}
                            onChangePlan={handleChangePlan}
                            onChangeStatus={handleChangeStatus}
                            onUpdateField={handleUpdateSubscriptionField}
                            formatDate={formatDate}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grant Lifetime Confirmation Modal */}
      {showGrantLifetime && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') setShowGrantLifetime(null); }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="grant-lifetime-dialog-title">
            <h3 id="grant-lifetime-dialog-title" className="text-lg font-semibold text-gray-800 mb-2">Grant Lifetime License</h3>
            <p className="text-gray-600 mb-1">
              This will give <strong>{teamsWithSubs.find(t => t.id === showGrantLifetime)?.name}</strong> permanent
              Business-tier access with no billing.
            </p>
            <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded mb-4">
              This action sets the team to Lifetime status. Their Stripe subscription (if any) will NOT be automatically canceled — you should cancel it in Stripe separately.
            </p>
            <div className="flex space-x-3">
              <Button variant="outline" size="md" onClick={() => setShowGrantLifetime(null)} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => handleGrantLifetime(showGrantLifetime)}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Granting...' : '👑 Grant Lifetime'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Credits Modal */}
      {showAddCredits && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') { setShowAddCredits(null); setCreditsToAdd(10); } }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="add-credits-dialog-title">
            <h3 id="add-credits-dialog-title" className="text-lg font-semibold text-gray-800 mb-2">Add Bonus Evaluation Credits</h3>
            <p className="text-gray-600 mb-3">
              Add credits to <strong>{teamsWithSubs.find(t => t.id === showAddCredits)?.name}</strong>.
              These are added on top of the plan limit and never expire.
            </p>
            <div className="mb-4">
              <label htmlFor="credits-input" className="block text-sm font-medium text-gray-700 mb-1">
                Credits to add
              </label>
              <input
                id="credits-input"
                type="number"
                min="1"
                max="1000"
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-2 mt-2">
                {[10, 25, 50, 100].map(n => (
                  <button
                    key={n}
                    onClick={() => setCreditsToAdd(n)}
                    className={`px-3 py-1 text-xs rounded border transition-colors ${
                      creditsToAdd === n
                        ? 'bg-blue-100 border-blue-400 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => { setShowAddCredits(null); setCreditsToAdd(10); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => handleAddCredits(showAddCredits)}
                disabled={saving || creditsToAdd <= 0}
                className="flex-1"
              >
                {saving ? 'Adding...' : `Add ${creditsToAdd} Credits`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpandedTeamDetail = ({ team, saving, onChangePlan, onChangeStatus, onUpdateField, formatDate }) => {
  const sub = team.sub;

  return (
    <div className="bg-gray-50 border-t border-b border-gray-200 p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Subscription Details */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Subscription</h4>
          {sub ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Plan</span>
                <select
                  value={sub.plan}
                  onChange={(e) => onChangePlan(team.id, e.target.value)}
                  disabled={saving}
                  className="text-sm border border-gray-300 rounded px-2 py-0.5"
                >
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="pro_plus">Business</option>
                </select>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <select
                  value={sub.status}
                  onChange={(e) => onChangeStatus(team.id, e.target.value)}
                  disabled={saving}
                  className="text-sm border border-gray-300 rounded px-2 py-0.5"
                >
                  <option value="active">Active</option>
                  <option value="trialing">Trialing</option>
                  <option value="past_due">Past Due</option>
                  <option value="canceled">Canceled</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Billing</span>
                <span className="text-gray-900 capitalize">
                  {sub.status === 'lifetime' ? 'None (Lifetime)' : sub.billingInterval ? `${sub.billingInterval}ly` : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Period Start</span>
                <span className="text-gray-900">{formatDate(sub.currentPeriodStart)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Period End</span>
                <span className="text-gray-900">{formatDate(sub.currentPeriodEnd)}</span>
              </div>
              {sub.trialEndsAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Trial Ends</span>
                  <span className="text-gray-900">
                    {formatDate(sub.trialEndsAt)}
                    {' '}
                    <span className="text-xs text-blue-600">({getTrialDaysRemaining(sub.trialEndsAt)}d left)</span>
                  </span>
                </div>
              )}
              {sub.grantedReason && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Grant Reason</span>
                  <span className="text-gray-900 text-xs">{sub.grantedReason}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No subscription configured</p>
          )}
        </div>

        {/* Usage & Limits */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Usage & Limits</h4>
          <div className="space-y-2 text-sm">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Evaluations</span>
                <span className="text-gray-900">{sub?.usage?.evaluationsUsed || 0} / {team.effectiveEvalLimit}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    team.effectiveEvalLimit > 0 && ((sub?.usage?.evaluationsUsed || 0) / team.effectiveEvalLimit) >= 0.9
                      ? 'bg-red-500'
                      : team.effectiveEvalLimit > 0 && ((sub?.usage?.evaluationsUsed || 0) / team.effectiveEvalLimit) >= 0.7
                        ? 'bg-yellow-500'
                        : 'bg-blue-500'
                  }`}
                  style={{ width: `${team.effectiveEvalLimit > 0 ? Math.min(100, ((sub?.usage?.evaluationsUsed || 0) / team.effectiveEvalLimit) * 100) : 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Agents</span>
                <span className="text-gray-900">
                  {team.agentCount} / {team.effectiveAgentLimit === null ? '∞' : team.effectiveAgentLimit}
                </span>
              </div>
              {team.effectiveAgentLimit !== null && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      team.effectiveAgentLimit > 0 && (team.agentCount / team.effectiveAgentLimit) >= 0.9
                        ? 'bg-red-500'
                        : team.effectiveAgentLimit > 0 && (team.agentCount / team.effectiveAgentLimit) >= 0.7
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                    }`}
                    style={{ width: `${team.effectiveAgentLimit > 0 ? Math.min(100, (team.agentCount / team.effectiveAgentLimit) * 100) : 0}%` }}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total Members</span>
              <span className="text-gray-900">{team.memberCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Last Usage Reset</span>
              <span className="text-gray-900">{formatDate(sub?.usage?.lastReset)}</span>
            </div>
          </div>
        </div>

        {/* Add-ons & Stripe */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Add-ons & Billing</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">White Labeling</span>
              <span className={team.whiteLabel ? 'text-green-600 font-medium' : 'text-gray-400'}>
                {team.whiteLabel ? '✓ Enabled' : '✗ Disabled'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Extra Agents</span>
              <span className="text-gray-900">{sub?.addons?.extraAgents || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Bonus Eval Credits</span>
              <span className="text-gray-900">{sub?.addons?.bonusEvalCredits || 0}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Stripe Customer</span>
                <span className="text-gray-900 text-xs font-mono">
                  {sub?.stripeCustomerId ? (
                    <a
                      href={`https://dashboard.stripe.com/customers/${sub.stripeCustomerId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sub.stripeCustomerId.slice(0, 18)}...
                    </a>
                  ) : '—'}
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-500">Stripe Sub</span>
                <span className="text-gray-900 text-xs font-mono">
                  {sub?.stripeSubscriptionId ? (
                    <a
                      href={`https://dashboard.stripe.com/subscriptions/${sub.stripeSubscriptionId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sub.stripeSubscriptionId.slice(0, 18)}...
                    </a>
                  ) : '—'}
                </span>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">{formatDate(sub?.createdAt)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-500">Updated</span>
                <span className="text-gray-900">{formatDate(sub?.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsAdmin;
